import { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  Plus, Trash2, Save, RefreshCw, Loader2, X, Settings2,
  Palette, Wrench, TrendingUp, Calendar, ChevronDown, ChevronRight, Eye
} from 'lucide-react';
import { useTheme } from '@/contexts/ThemeContext';
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip
} from 'recharts';

// ─── Disciplines Section ───
function DisciplinesPanel({ projectId }) {
  const [disciplines, setDisciplines] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ disciplineCode: '', disciplineName: '', displayColor: '#4A90D9', sequenceOrder: 0 });

  const fetch = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get(`/v1/projects/${projectId}/fc/disciplines`); setDisciplines(data); }
    catch { /* ignore */ }
    setLoading(false);
  }, [projectId]);

  useEffect(() => { fetch(); }, [fetch]);

  const seed = async () => {
    try { await api.post(`/v1/projects/${projectId}/fc/disciplines/seed-defaults`); toast.success('Disciplines seeded'); fetch(); }
    catch { toast.error('Seed failed'); }
  };

  const add = async () => {
    if (!form.disciplineCode || !form.disciplineName) { toast.error('Code and name required'); return; }
    try { await api.post(`/v1/projects/${projectId}/fc/disciplines`, form); toast.success('Created'); setShowAdd(false); setForm({ disciplineCode: '', disciplineName: '', displayColor: '#4A90D9', sequenceOrder: 0 }); fetch(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };

  const remove = async (id) => {
    try { await api.delete(`/v1/projects/${projectId}/fc/disciplines/${id}`); toast.success('Deleted'); fetch(); }
    catch { toast.error('Delete failed'); }
  };

  return (
    <div className="space-y-3" data-testid="fc-disciplines-panel">
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-semibold flex items-center gap-2" style={{ color: 'var(--text-primary)' }}>
          <Palette className="w-4 h-4" /> Disciplines ({disciplines.length})
        </h4>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={seed} data-testid="seed-disciplines-btn">Seed Defaults</Button>
          <Button size="sm" onClick={() => setShowAdd(!showAdd)} data-testid="add-discipline-btn"><Plus className="w-3.5 h-3.5 mr-1" /> Add</Button>
        </div>
      </div>

      {showAdd && (
        <div className="grid grid-cols-4 gap-2 p-3 rounded-lg border" style={{ background: 'var(--card-bg)', borderColor: 'var(--border-color)' }}>
          <input placeholder="Code (e.g. CIV)" value={form.disciplineCode} onChange={e => setForm({ ...form, disciplineCode: e.target.value.toUpperCase() })}
            className="px-2 py-1.5 rounded text-sm border" style={{ background: 'var(--input-bg)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }} />
          <input placeholder="Name" value={form.disciplineName} onChange={e => setForm({ ...form, disciplineName: e.target.value })}
            className="px-2 py-1.5 rounded text-sm border" style={{ background: 'var(--input-bg)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }} />
          <div className="flex items-center gap-2">
            <input type="color" value={form.displayColor} onChange={e => setForm({ ...form, displayColor: e.target.value })} className="w-8 h-8 rounded cursor-pointer" />
            <input type="number" placeholder="Order" value={form.sequenceOrder} onChange={e => setForm({ ...form, sequenceOrder: parseInt(e.target.value) || 0 })}
              className="w-20 px-2 py-1.5 rounded text-sm border" style={{ background: 'var(--input-bg)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }} />
          </div>
          <div className="flex gap-1">
            <Button size="sm" onClick={add}>Save</Button>
            <Button size="sm" variant="ghost" onClick={() => setShowAdd(false)}><X className="w-3.5 h-3.5" /></Button>
          </div>
        </div>
      )}

      {loading ? <Loader2 className="w-5 h-5 animate-spin mx-auto" style={{ color: 'var(--text-muted)' }} /> : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
          {disciplines.map(d => (
            <div key={d.id} className="flex items-center justify-between p-3 rounded-lg border" style={{ background: 'var(--card-bg)', borderColor: 'var(--border-color)' }}>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded" style={{ background: d.displayColor }} />
                <div>
                  <span className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>{d.disciplineCode}</span>
                  <span className="text-xs ml-2" style={{ color: 'var(--text-muted)' }}>{d.disciplineName}</span>
                </div>
              </div>
              <button onClick={() => remove(d.id)} className="opacity-50 hover:opacity-100" data-testid={`delete-disc-${d.disciplineCode}`}>
                <Trash2 className="w-3.5 h-3.5" style={{ color: '#ef4444' }} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Activity Types Section ───
function ActivityTypesPanel({ projectId }) {
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [discFilter, setDiscFilter] = useState('');
  const [disciplines, setDisciplines] = useState([]);
  const [form, setForm] = useState({ disciplineCode: '', activityTypeCode: '', activityTypeName: '', defaultUOM: 'NOS', manhourFactor: 1.0, defaultDistributionCurve: 'BELL_CURVE' });

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const params = discFilter ? `?discipline=${discFilter}` : '';
      const { data } = await api.get(`/v1/projects/${projectId}/fc/activity-types${params}`);
      setTypes(data);
    } catch { /* ignore */ }
    setLoading(false);
  }, [projectId, discFilter]);

  useEffect(() => { fetch(); }, [fetch]);
  useEffect(() => {
    api.get(`/v1/projects/${projectId}/fc/disciplines`).then(r => setDisciplines(r.data)).catch(() => {});
  }, [projectId]);

  const seed = async () => {
    try { await api.post(`/v1/projects/${projectId}/fc/activity-types/seed-defaults`); toast.success('Activity types seeded'); fetch(); }
    catch { toast.error('Seed failed'); }
  };

  const add = async () => {
    if (!form.activityTypeCode || !form.activityTypeName || !form.disciplineCode) { toast.error('All fields required'); return; }
    try { await api.post(`/v1/projects/${projectId}/fc/activity-types`, form); toast.success('Created'); setShowAdd(false); fetch(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };

  const remove = async (id) => {
    try { await api.delete(`/v1/projects/${projectId}/fc/activity-types/${id}`); toast.success('Deleted'); fetch(); }
    catch { toast.error('Delete failed'); }
  };

  return (
    <div className="space-y-3" data-testid="fc-activity-types-panel">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h4 className="text-sm font-semibold flex items-center gap-2" style={{ color: 'var(--text-primary)' }}>
          <Wrench className="w-4 h-4" /> Activity Types ({types.length})
        </h4>
        <div className="flex gap-2 items-center">
          <select value={discFilter} onChange={e => setDiscFilter(e.target.value)}
            className="px-2 py-1.5 rounded text-xs border" style={{ background: 'var(--input-bg)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}>
            <option value="">All Disciplines</option>
            {disciplines.map(d => <option key={d.disciplineCode} value={d.disciplineCode}>{d.disciplineCode}</option>)}
          </select>
          <Button size="sm" variant="outline" onClick={seed} data-testid="seed-types-btn">Seed Defaults</Button>
          <Button size="sm" onClick={() => setShowAdd(!showAdd)} data-testid="add-type-btn"><Plus className="w-3.5 h-3.5 mr-1" /> Add</Button>
        </div>
      </div>

      {showAdd && (
        <div className="grid grid-cols-6 gap-2 p-3 rounded-lg border" style={{ background: 'var(--card-bg)', borderColor: 'var(--border-color)' }}>
          <select value={form.disciplineCode} onChange={e => setForm({ ...form, disciplineCode: e.target.value })}
            className="px-2 py-1.5 rounded text-xs border" style={{ background: 'var(--input-bg)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}>
            <option value="">Discipline</option>
            {disciplines.map(d => <option key={d.disciplineCode} value={d.disciplineCode}>{d.disciplineCode}</option>)}
          </select>
          <input placeholder="Code" value={form.activityTypeCode} onChange={e => setForm({ ...form, activityTypeCode: e.target.value.toUpperCase() })}
            className="px-2 py-1.5 rounded text-xs border" style={{ background: 'var(--input-bg)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }} />
          <input placeholder="Name" value={form.activityTypeName} onChange={e => setForm({ ...form, activityTypeName: e.target.value })}
            className="px-2 py-1.5 rounded text-xs border" style={{ background: 'var(--input-bg)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }} />
          <input placeholder="UOM" value={form.defaultUOM} onChange={e => setForm({ ...form, defaultUOM: e.target.value })}
            className="px-2 py-1.5 rounded text-xs border" style={{ background: 'var(--input-bg)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }} />
          <input type="number" step="0.1" placeholder="MH Factor" value={form.manhourFactor} onChange={e => setForm({ ...form, manhourFactor: parseFloat(e.target.value) || 1 })}
            className="px-2 py-1.5 rounded text-xs border" style={{ background: 'var(--input-bg)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }} />
          <div className="flex gap-1">
            <Button size="sm" onClick={add}>Save</Button>
            <Button size="sm" variant="ghost" onClick={() => setShowAdd(false)}><X className="w-3.5 h-3.5" /></Button>
          </div>
        </div>
      )}

      {loading ? <Loader2 className="w-5 h-5 animate-spin mx-auto" style={{ color: 'var(--text-muted)' }} /> : (
        <div className="overflow-x-auto rounded-lg border" style={{ borderColor: 'var(--border-color)' }}>
          <table className="w-full text-xs">
            <thead>
              <tr style={{ background: 'var(--card-bg-alt)' }}>
                <th className="text-left p-2 font-medium" style={{ color: 'var(--text-muted)' }}>Code</th>
                <th className="text-left p-2 font-medium" style={{ color: 'var(--text-muted)' }}>Name</th>
                <th className="text-left p-2 font-medium" style={{ color: 'var(--text-muted)' }}>Disc</th>
                <th className="text-left p-2 font-medium" style={{ color: 'var(--text-muted)' }}>UOM</th>
                <th className="text-right p-2 font-medium" style={{ color: 'var(--text-muted)' }}>MH Factor</th>
                <th className="text-left p-2 font-medium" style={{ color: 'var(--text-muted)' }}>Curve</th>
                <th className="p-2 w-8"></th>
              </tr>
            </thead>
            <tbody>
              {types.map(t => (
                <tr key={t.id} className="border-t" style={{ borderColor: 'var(--border-color)' }}>
                  <td className="p-2 font-mono font-medium" style={{ color: 'var(--text-primary)' }}>{t.activityTypeCode}</td>
                  <td className="p-2" style={{ color: 'var(--text-primary)' }}>{t.activityTypeName}</td>
                  <td className="p-2" style={{ color: 'var(--text-muted)' }}>{t.disciplineCode}</td>
                  <td className="p-2" style={{ color: 'var(--text-muted)' }}>{t.defaultUOM}</td>
                  <td className="p-2 text-right font-mono" style={{ color: 'var(--text-primary)' }}>{t.manhourFactor}</td>
                  <td className="p-2"><span className="px-1.5 py-0.5 rounded text-[10px] font-medium" style={{ background: 'var(--card-bg-alt)', color: 'var(--accent-primary)' }}>{t.defaultDistributionCurve}</span></td>
                  <td className="p-2"><button onClick={() => remove(t.id)} className="opacity-40 hover:opacity-100"><Trash2 className="w-3 h-3" style={{ color: '#ef4444' }} /></button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ─── Distribution Curves Section ───
function CurvesPanel({ projectId }) {
  const { isDark } = useTheme();
  const [curves, setCurves] = useState([]);
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [previewCurve, setPreviewCurve] = useState(null);

  const fetch = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get(`/v1/projects/${projectId}/fc/distribution-curves`); setCurves(data); }
    catch { /* ignore */ }
    setLoading(false);
  }, [projectId]);

  useEffect(() => { fetch(); }, [fetch]);

  const loadPreview = async (curve) => {
    setPreviewCurve(curve);
    try {
      const { data } = await api.post('/v1/fc/distribution-curves/preview', {
        formulaType: curve.formulaType,
        betaParams: curve.betaParams || null,
        controlPoints: curve.controlPoints || null,
        scopeQuantity: 1000,
        planStartDate: '2025-01-01',
        planFinishDate: '2025-12-31',
        periodType: 'MONTHLY'
      });
      setPreview(data);
    } catch { toast.error('Preview failed'); }
  };

  return (
    <div className="space-y-3" data-testid="fc-curves-panel">
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-semibold flex items-center gap-2" style={{ color: 'var(--text-primary)' }}>
          <TrendingUp className="w-4 h-4" /> Distribution Curves ({curves.length})
        </h4>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        {curves.map(c => (
          <div key={c.curveCode} className="p-3 rounded-lg border cursor-pointer transition-all hover:scale-[1.02]"
            style={{ background: previewCurve?.curveCode === c.curveCode ? 'var(--card-bg-alt)' : 'var(--card-bg)', borderColor: previewCurve?.curveCode === c.curveCode ? 'var(--accent-primary)' : 'var(--border-color)' }}
            onClick={() => loadPreview(c)} data-testid={`curve-${c.curveCode}`}>
            <div className="flex items-center justify-between mb-1">
              <span className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>{c.curveName}</span>
              <span className="text-[10px] px-1.5 py-0.5 rounded" style={{ background: c.curveType === 'SYSTEM' ? '#3b82f620' : '#f59e0b20', color: c.curveType === 'SYSTEM' ? '#3b82f6' : '#f59e0b' }}>
                {c.curveType}
              </span>
            </div>
            <p className="text-xs" style={{ color: 'var(--text-muted)' }}>{c.description}</p>
            {c.betaParams && <p className="text-[10px] mt-1 font-mono" style={{ color: 'var(--text-muted)' }}>alpha={c.betaParams.alpha} beta={c.betaParams.beta}</p>}
            <button className="flex items-center gap-1 mt-2 text-[10px]" style={{ color: 'var(--accent-primary)' }}>
              <Eye className="w-3 h-3" /> Preview
            </button>
          </div>
        ))}
      </div>

      {preview && previewCurve && (
        <div className="p-4 rounded-lg border" style={{ background: 'var(--card-bg)', borderColor: 'var(--border-color)' }}>
          <div className="flex items-center justify-between mb-3">
            <h5 className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>
              Curve Preview: {previewCurve.curveName} (Scope: 1,000)
            </h5>
            <button onClick={() => { setPreview(null); setPreviewCurve(null); }}><X className="w-4 h-4" style={{ color: 'var(--text-muted)' }} /></button>
          </div>
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={preview.curvePreviewPoints} margin={{ top: 5, right: 20, bottom: 5, left: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={isDark ? '#1e293b' : '#e2e8f0'} />
              <XAxis dataKey="t" tick={{ fontSize: 10, fill: isDark ? '#94a3b8' : '#64748b' }} tickFormatter={v => `${(v * 100).toFixed(0)}%`} />
              <YAxis tick={{ fontSize: 10, fill: isDark ? '#94a3b8' : '#64748b' }} domain={[0, 100]} tickFormatter={v => `${v}%`} />
              <Tooltip contentStyle={{ background: isDark ? '#1e293b' : '#fff', border: '1px solid var(--border-color)', borderRadius: 8, fontSize: 11 }}
                formatter={v => [`${v}%`, 'Cumulative']} labelFormatter={v => `Time: ${(v * 100).toFixed(0)}%`} />
              <Line type="monotone" dataKey="cumPct" stroke="#3B82F6" strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}

// ─── Reporting Periods Section ───
function ReportingPeriodsPanel({ projectId }) {
  const [periods, setPeriods] = useState(null);
  const [loading, setLoading] = useState(false);
  const [genForm, setGenForm] = useState({ periodType: 'DAILY', projectStartDate: '2025-01-01', projectEndDate: '2026-12-31' });
  const [expanded, setExpanded] = useState(false);

  const fetch = useCallback(async () => {
    try { const { data } = await api.get(`/v1/projects/${projectId}/fc/reporting-periods`); setPeriods(data); }
    catch { /* ignore */ }
  }, [projectId]);

  useEffect(() => { fetch(); }, [fetch]);

  const generate = async () => {
    setLoading(true);
    try {
      await api.post(`/v1/projects/${projectId}/fc/reporting-periods/generate`, genForm);
      toast.success('Reporting periods generated');
      fetch();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
    setLoading(false);
  };

  const closePeriod = async (periodId) => {
    try { await api.put(`/v1/projects/${projectId}/fc/reporting-periods/${periodId}/close`); toast.success('Period closed'); fetch(); }
    catch { toast.error('Failed'); }
  };

  const reopenPeriod = async (periodId) => {
    try { await api.put(`/v1/projects/${projectId}/fc/reporting-periods/${periodId}/reopen`); toast.success('Period reopened'); fetch(); }
    catch { toast.error('Failed'); }
  };

  const periodsList = periods?.periods || [];

  return (
    <div className="space-y-3" data-testid="fc-periods-panel">
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-semibold flex items-center gap-2" style={{ color: 'var(--text-primary)' }}>
          <Calendar className="w-4 h-4" /> Reporting Periods ({periodsList.length})
        </h4>
      </div>

      <div className="p-3 rounded-lg border" style={{ background: 'var(--card-bg)', borderColor: 'var(--border-color)' }}>
        <p className="text-xs mb-2" style={{ color: 'var(--text-muted)' }}>Generate monthly or weekly reporting periods for the project timeline</p>
        <div className="flex items-end gap-3 flex-wrap">
          <div>
            <label className="text-[10px] font-medium block mb-1" style={{ color: 'var(--text-muted)' }}>Type</label>
            <select value={genForm.periodType} onChange={e => setGenForm({ ...genForm, periodType: e.target.value })}
              className="px-2 py-1.5 rounded text-xs border" style={{ background: 'var(--input-bg)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}>
              <option value="DAILY">Daily</option>
              <option value="MONTHLY">Monthly</option>
              <option value="WEEKLY">Weekly</option>
            </select>
          </div>
          <div>
            <label className="text-[10px] font-medium block mb-1" style={{ color: 'var(--text-muted)' }}>Start Date</label>
            <input type="date" value={genForm.projectStartDate} onChange={e => setGenForm({ ...genForm, projectStartDate: e.target.value })}
              className="px-2 py-1.5 rounded text-xs border" style={{ background: 'var(--input-bg)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }} />
          </div>
          <div>
            <label className="text-[10px] font-medium block mb-1" style={{ color: 'var(--text-muted)' }}>End Date</label>
            <input type="date" value={genForm.projectEndDate} onChange={e => setGenForm({ ...genForm, projectEndDate: e.target.value })}
              className="px-2 py-1.5 rounded text-xs border" style={{ background: 'var(--input-bg)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }} />
          </div>
          <Button size="sm" onClick={generate} disabled={loading} data-testid="generate-periods-btn">
            {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5 mr-1" />} Generate
          </Button>
        </div>
      </div>

      {periodsList.length > 0 && (
        <div className="rounded-lg border overflow-hidden" style={{ borderColor: 'var(--border-color)' }}>
          <button onClick={() => setExpanded(!expanded)} className="w-full flex items-center justify-between p-3 text-xs font-medium"
            style={{ background: 'var(--card-bg-alt)', color: 'var(--text-muted)' }}>
            <span>{periodsList.length} periods ({periods?.periodType || 'MONTHLY'})</span>
            {expanded ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
          </button>
          {expanded && (
            <div className="max-h-64 overflow-y-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr style={{ background: 'var(--card-bg-alt)' }}>
                    <th className="text-left p-2 font-medium" style={{ color: 'var(--text-muted)' }}>Period</th>
                    <th className="text-left p-2 font-medium" style={{ color: 'var(--text-muted)' }}>Start</th>
                    <th className="text-left p-2 font-medium" style={{ color: 'var(--text-muted)' }}>End</th>
                    <th className="text-center p-2 font-medium" style={{ color: 'var(--text-muted)' }}>Status</th>
                    <th className="p-2 w-20"></th>
                  </tr>
                </thead>
                <tbody>
                  {periodsList.map(p => (
                    <tr key={p.periodId} className="border-t" style={{ borderColor: 'var(--border-color)', background: p.isCurrent ? 'var(--card-bg-alt)' : 'transparent' }}>
                      <td className="p-2 font-medium" style={{ color: 'var(--text-primary)' }}>
                        {p.periodLabel} {p.isCurrent && <span className="text-[9px] px-1 py-0.5 rounded ml-1" style={{ background: '#3b82f620', color: '#3b82f6' }}>CURRENT</span>}
                      </td>
                      <td className="p-2" style={{ color: 'var(--text-muted)' }}>{p.startDate?.slice(0, 10)}</td>
                      <td className="p-2" style={{ color: 'var(--text-muted)' }}>{p.endDate?.slice(0, 10)}</td>
                      <td className="p-2 text-center">
                        <span className="px-1.5 py-0.5 rounded text-[10px] font-medium"
                          style={{ background: p.isClosed ? '#ef444420' : '#10b98120', color: p.isClosed ? '#ef4444' : '#10b981' }}>
                          {p.isClosed ? 'CLOSED' : 'OPEN'}
                        </span>
                      </td>
                      <td className="p-2">
                        {p.isClosed
                          ? <button onClick={() => reopenPeriod(p.periodId)} className="text-[10px] underline" style={{ color: '#f59e0b' }}>Reopen</button>
                          : <button onClick={() => closePeriod(p.periodId)} className="text-[10px] underline" style={{ color: 'var(--text-muted)' }}>Close</button>
                        }
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Main Configuration Component ───
export default function FCConfiguration({ projectId }) {
  const [section, setSection] = useState('disciplines');

  const sections = [
    { id: 'disciplines', label: 'Disciplines', icon: Palette },
    { id: 'types', label: 'Activity Types', icon: Wrench },
    { id: 'curves', label: 'Distribution Curves', icon: TrendingUp },
    { id: 'periods', label: 'Reporting Periods', icon: Calendar },
  ];

  return (
    <div className="space-y-4" data-testid="fc-configuration">
      <div className="flex items-center gap-1 overflow-x-auto" data-testid="config-sub-tabs">
        {sections.map(s => {
          const Icon = s.icon;
          return (
            <button key={s.id} onClick={() => setSection(s.id)}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg whitespace-nowrap transition-colors"
              style={{
                color: section === s.id ? '#fff' : 'var(--text-muted)',
                background: section === s.id ? 'var(--accent-primary)' : 'var(--card-bg)',
                border: `1px solid ${section === s.id ? 'var(--accent-primary)' : 'var(--border-color)'}`
              }}
              data-testid={`config-tab-${s.id}`}>
              <Icon className="w-3 h-3" /> {s.label}
            </button>
          );
        })}
      </div>

      {section === 'disciplines' && <DisciplinesPanel projectId={projectId} />}
      {section === 'types' && <ActivityTypesPanel projectId={projectId} />}
      {section === 'curves' && <CurvesPanel projectId={projectId} />}
      {section === 'periods' && <ReportingPeriodsPanel projectId={projectId} />}
    </div>
  );
}
